import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import { useDebounce } from "@rentiohq/shared-frontend/dist/hooks/useDebounce";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import { getContactMeMaster } from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as documentHooks from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.hooks";
import {
  EExportActionType,
  EExportFileType,
  EExportType,
} from "@rentiohq/shared-frontend/dist/reduxV2/exportFile";
import * as exportFilesActions from "@rentiohq/shared-frontend/dist/reduxV2/exportFile/exportFile.actions";
import * as beneficiaryReportHooks from "@rentiohq/shared-frontend/dist/reduxV2/reportBeneficiary/report.beneficiary.hooks";
import { EQueryFilterAlias } from "@rentiohq/shared-frontend/dist/reduxV2/utils/api.types";
import { IDocument } from "@rentiohq/shared-frontend/dist/types/document.types";
import { EPaymentOrderOwnerPaymentMethod } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { IBeneficiaryReportPayout } from "@rentiohq/shared-frontend/dist/types/report.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { join } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import {
  ActionButton,
  Card,
  DataTable,
  ELoadingSize,
  EmptyState,
  Loading,
  mapItemsToDataTableProps,
  Pagination,
} from "@rentiohq/web-shared/dist/components";
import { useMultiselect } from "@rentiohq/web-shared/dist/hooks/useMultiselect";
import { EPreferencePersistScope } from "@rentiohq/web-shared/dist/redux/system/system.types";
import { flatten, sortBy, uniq } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import usePreference from "scenes/Settings/hooks/usePreference";
import { NumberParam, StringParam } from "serialize-query-params";
import {
  BENEFICIARY_REPORTS_CUTOFF_DATE,
  BeneficiaryReportFilter,
  IResultingFilter,
} from "./BeneficiaryReport.filter";
import { EBeneficiaryReportReported } from "./BeneficiaryReport.types";
import { getItems, useLinkedPayouts } from "./BeneficiaryReport.util";

const SELECTION_LIMIT = 500;

// Filter shared by the "not reported" / "reported" tile counts.
const COUNT_FILTER = {
  createdAt: { gte: BENEFICIARY_REPORTS_CUTOFF_DATE },
  requestPayeeAccountId: { notIn: EQueryFilterAlias.ActiveAccountIds },
};

// POST /v2/export-files only queues the report (status NEW); a serverless worker
// writes the paymentRequestDocument rows that make a payout "reported" after the
// 201. The list and both tile counts are therefore refetched again on this short
// bounded schedule, so they catch the worker's result without polling.
const REPORT_REFRESH_DELAYS_MS = [3000, 8000];

export const BeneficiaryReport = () => {
  const [, setQueryParamValue] = useQueryParams({
    paymentRequestId: NumberParam,
    documentId: StringParam,
  });
  const dispatch = useDispatch();

  const contactMeMaster = useSelector((state: IRootStore) =>
    getContactMeMaster(state),
  );

  const [page, setPage] = useState<number>(1);
  const {
    selectedIds,
    onSelectIds,
    onDeselectIds,
    onSelectId,
    onDeselectId,
    onDeselectAll,
  } = useMultiselect<number>([], SELECTION_LIMIT);

  const [searchQuery = "", setSearchQuery] = usePreference({
    preferenceKey: "search_query",
    preferencePersistScope: EPreferencePersistScope.LocalStorage,
  });
  const [queryDebounced] = useDebounce(searchQuery);

  const [payoutToGetDocsFor, setPayoutToGetDocsFor] = useState<number>();

  // TODO: might be better to store this in redux -> this is only a temp solution
  // const allPayoutsRef = useRef<IBeneficiaryReportPayout[]>([]);
  const [filter, setFilter] = useState<IResultingFilter>();

  useEffect(() => {
    onDeselectAll();
    setPage(1);
  }, [queryDebounced, filter]);

  const {
    items: payouts,
    isFetching,
    totalPages = 0,
    refetch,
  } = beneficiaryReportHooks.usePaged(
    filter
      ? {
          shouldRefetch: true,
          query: {
            page,
            limit: CONFIG.DEFAULT_FETCH_LIMIT,
            sort: [
              { field: "requestPayeeAccountId", method: "ASC" },
              { field: "payoutDate", method: "DESC" },
            ],
            search: queryDebounced,
            ...filter,
          },
        }
      : {
          shouldRefetch: true,
        },
  );
  // Counts for the "not reported" / "reported" tiles. They live here, next to
  // the list, so that creating a report can refetch all three together.
  const { count: notReportedCount, refetch: refetchNotReportedCount } =
    beneficiaryReportHooks.useCount({
      shouldRefetch: true,
      query: contactMeMaster
        ? {
            filter: COUNT_FILTER,
            customFilters: [
              EBeneficiaryReportReported.PaymentRequestNotReported,
            ],
          }
        : undefined,
    });
  const { count: reportedCount, refetch: refetchReportedCount } =
    beneficiaryReportHooks.useCount({
      shouldRefetch: true,
      query: contactMeMaster
        ? {
            filter: COUNT_FILTER,
            customFilters: [EBeneficiaryReportReported.PaymentRequestReported],
          }
        : undefined,
    });

  // The refetch functions early-return on the `isFetching` captured at render
  // time, so a timer must call the latest ones instead of the closure it was
  // created with.
  const latestRefetchRef = useRef({
    refetch,
    refetchNotReportedCount,
    refetchReportedCount,
  });
  latestRefetchRef.current = {
    refetch,
    refetchNotReportedCount,
    refetchReportedCount,
  };
  const refreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearRefreshTimers = () => {
    refreshTimersRef.current.forEach(clearTimeout);
    refreshTimersRef.current = [];
  };

  const refreshReportData = () => {
    const { current } = latestRefetchRef;
    current.refetch();
    current.refetchNotReportedCount();
    current.refetchReportedCount();
  };

  useEffect(() => clearRefreshTimers, []);

  const allLinkedPayouts = useLinkedPayouts({
    payoutIdsToGetAndFetch: payouts?.map(p => p.id),
    payoutIdsToGet: selectedIds,
  });

  const { items: documentsForPaymentRequest, isFetching: isFetchingDocuments } =
    documentHooks.useGetAll({
      shouldRefetch: true,
      customPath: payoutToGetDocsFor
        ? `/payment-requests/${payoutToGetDocsFor}/documents`
        : undefined,
      query: payoutToGetDocsFor ? {} : undefined,
    });

  const items = getItems({ allLinkedPayouts });
  const tableSetup = mapItemsToDataTableProps(items, {
    hasActions: true,
  });

  const createReport = (
    selectedPayoutIds: number[],
    action: EExportActionType,
  ) => {
    if (selectedPayoutIds?.length === 0) {
      return;
    }

    const missingLinkedPayoutsForPayoutIds: number[] = [];

    const linkedPayoutIds = flatten(
      selectedPayoutIds.map(payoutId => {
        const linkedPayouts = allLinkedPayouts[payoutId]?.items;
        if (linkedPayouts) {
          return linkedPayouts
            .filter(
              x =>
                x.paymentRequestItems?.[0]?.paymentOrder.ownerPaymentMethod ===
                EPaymentOrderOwnerPaymentMethod.FromRent,
            )
            .map(x => x.id);
        }

        missingLinkedPayoutsForPayoutIds.push(payoutId);
        return [];
      }),
    );

    if (missingLinkedPayoutsForPayoutIds.length > 0) {
      showAlert({
        type: "error",
        message: "Incomplete data",
        content: `Missing linked payouts for ${join(
          missingLinkedPayoutsForPayoutIds.map(String),
        )}`,
      });
      return;
    }

    // Two quick creates must not stack their refresh schedules.
    clearRefreshTimers();

    dispatch(
      exportFilesActions.createStart.getAction({
        data: {
          exportFileType: EExportFileType.Pdf,
          exportType: EExportType.BeneficiaryReport,
          exportParameters: {
            exportBeneficiaryReportParameters: {
              paymentRequestIds: uniq([
                ...selectedPayoutIds,
                ...linkedPayoutIds,
              ]),
              action,
            },
          },
        },
        onSuccess: () => {
          onDeselectAll();
          // INFO: see REPORT_REFRESH_DELAYS_MS - the immediate refetch races the
          // worker, the delayed ones pick up its result.
          refreshReportData();
          refreshTimersRef.current = REPORT_REFRESH_DELAYS_MS.map(delay =>
            setTimeout(refreshReportData, delay),
          );
        },
      }),
    );
  };

  const getRows = () => {
    if (!payouts) {
      return [];
    }

    return payouts.map((payout: IBeneficiaryReportPayout) => {
      const actions = [];

      const linkedPayouts = allLinkedPayouts[payout.id]?.items;

      actions.push([
        {
          content: getLocalizedText(
            "payments.follow_up.card_actions.detail_payment_request",
          ),
          onClick: () => {
            setQueryParamValue({
              paymentRequestId: payout.id,
            });
          },
        },
        {
          content: getLocalizedText(
            "payments.follow_up.card_actions.detail_payment_request_parent",
          ),
          onClick: () => {
            setQueryParamValue({
              paymentRequestId: payout.parent,
            });
          },
        },
      ]);

      const canCreateReport = !!linkedPayouts && payout.reportingEnabled;

      if (canCreateReport) {
        actions.push([
          {
            content: getLocalizedText("reports.beneficiary.create_report"),
            onClick: () => createReport([payout.id], EExportActionType.Create),
          },
          {
            content: getLocalizedText("reports.beneficiary.create_and_send"),
            onClick: () =>
              createReport([payout.id], EExportActionType.CreateAndEmail),
          },
        ]);
      }

      const docs = documentsForPaymentRequest ?? [];

      if (isFetchingDocuments) {
        actions.push([
          {
            content: <Loading asDots={true} size={ELoadingSize.Medium} />,
          },
        ]);
      } else {
        actions.push(
          sortBy(docs || [], "createdAt").map((doc: IDocument) => ({
            content: doc.filename,
            onClick: () => {
              setQueryParamValue({
                documentId: doc.id,
              });
            },
          })),
        );
      }

      return {
        id: payout.id,
        multiSelectDisabled: !canCreateReport,
        multiSelectDisabledTooltip: !linkedPayouts
          ? getLocalizedText("system.loading")
          : getLocalizedText("reports.beneficiary.reporting_disabled", {
              beneficiaryName: getName(payout.payeeAccount),
            }),
        content: items
          .map(item => item.renderContent(payout))
          .filter(c => c !== null),
        actions,
        onDropdownOpen: () => {
          // Forces the call to happen every time the dropdown is opened.
          setPayoutToGetDocsFor(undefined);
          setPayoutToGetDocsFor(payout.id);
        },
      };
    });
  };

  const renderPaging = () => {
    if (totalPages <= 1) {
      return null;
    }

    return (
      <Pagination
        initialPage={page ? page - 1 : 0}
        pageCount={totalPages}
        onPageChange={({ selected }) => setPage(selected + 1)}
      />
    );
  };

  const handleShowConfirmModal = () => {
    confirm({
      title: getLocalizedText("reports.beneficiary_report.confirm.title"),
      primaryActions: [
        {
          title: getLocalizedText(
            "reports.beneficiary_report.cta.without_email",
          ),
          onPress: () => {
            createReport(selectedIds, EExportActionType.Create);
          },
        },
        {
          title: getLocalizedText("reports.beneficiary_report.cta.with_email"),
          onPress: () => {
            createReport(selectedIds, EExportActionType.CreateAndEmail);
          },
        },
      ],
    });
  };

  return (
    <Card heading={getLocalizedText("reports.beneficiary.title")}>
      <BeneficiaryReportFilter
        notReportedCount={notReportedCount}
        reportedCount={reportedCount}
        onFilterChange={newFilter => {
          setFilter(newFilter);
        }}
        onSearchChange={setSearchQuery}
        searchQuery={searchQuery}
      />

      <Spacer weight={ESpacerWeight.W16} />

      {selectedIds && selectedIds.length > 0 && (
        <ActionButton
          title={getLocalizedText("reports.beneficiary.create_report")}
          value={selectedIds.length}
          onAction={handleShowConfirmModal}
          onClear={() => onDeselectAll()}
        />
      )}

      <DataTable
        isLoadingData={isFetching && !payouts}
        horizontalSpacing="extraTight"
        emptyState={
          <EmptyState
            backgroundColor="gray"
            heading={getLocalizedText("reports.beneficiary.payouts.empty")}
          />
        }
        {...tableSetup}
        rows={getRows()}
        multiSelect={true}
        selectedIds={selectedIds}
        onSelectPage={(ids: (number | string)[]) => {
          // TODO: Can select?
          onSelectIds(ids as number[]);
        }}
        onDeselectPage={(ids: (number | string)[]) => {
          onDeselectIds(ids as number[]);
        }}
        onSelectRow={(id: number | string) => {
          onSelectId(id as number);
        }}
        onDeselectRow={(id: number | string) => {
          onDeselectId(id as number);
        }}
        onRowClick={rowIndex => {
          setQueryParamValue({
            paymentRequestId: payouts?.[rowIndex].id,
          });
        }}
      />

      {renderPaging()}
    </Card>
  );
};
